import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/apiService";
import { useOrder } from "../context/OrderContext";

import {
  QrCode,
  BarChart3,
  Menu as MenuIcon,
  ShoppingCart,
} from "lucide-react";
import DashboardHeader from "./DashboardHeader";
import OverviewStats from "./OverviewStats";
import OrdersList from "./OrdersList";
import MenuManager from "./MenuManager";
import QRCodeSection from "./QRCodeSection";

const RestaurantDashboard = () => {
  const [isUpdatingRestaurant, setIsUpdatingRestaurant] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState("");
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [activeTab, setActiveTab] = useState("menu");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRestaurant, setEditRestaurant] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      localStorage.setItem("token", token);
      // Optionally, clean up the URL
      window.history.replaceState({}, document.title, "/restaurant-dashboard");
    }
  }, []);

  // ...rest of your dashboard code

  // Handler to open edit modal
  const handleEditRestaurant = () => {
    setEditRestaurant({
      restaurantName: restaurant?.restaurantName || "",
      address: restaurant?.address || "",
      phoneNumber: restaurant?.phoneNumber || "",
      cuisineType: restaurant?.cuisineType || "",
      email: restaurant?.email || "",
      logo: restaurant?.logo || "",
      openingTime: restaurant?.openingTime || "",
      closingTime: restaurant?.closingTime || "",
    });
    setLogoFile(null);
    setShowEditModal(true);
  };

  // Handler to update restaurant info
  const handleUpdateRestaurant = async () => {
    if (!restaurant || !restaurant.id) return;
    const token = localStorage.getItem("token");
    setLogoUploadError("");
    setIsUpdatingRestaurant(true);
    try {
      let logoUrl = editRestaurant.logo;
      if (logoFile) {
        setLogoUploading(true);
        try {
          const logoForm = new FormData();
          logoForm.append("file", logoFile);
          const uploadRes = await api.post("cloudinary/upload", logoForm, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          });
          let url = null;
          if (uploadRes.data) {
            if (
              typeof uploadRes.data === "string" &&
              uploadRes.data.startsWith("http")
            ) {
              url = uploadRes.data;
            } else if (uploadRes.data.url) {
              url = uploadRes.data.url;
            } else if (uploadRes.data.data && uploadRes.data.data.url) {
              url = uploadRes.data.data.url;
            }
          }
          if (url) {
            logoUrl = url;
          } else {
            setLogoUploadError(
              "Upload failed: No URL returned. Response: " +
                JSON.stringify(uploadRes.data)
            );
            setIsUpdatingRestaurant(false);
            return;
          }
        } catch (err) {
          setLogoUploadError("Image upload failed");
          setIsUpdatingRestaurant(false);
          return;
        } finally {
          setLogoUploading(false);
        }
      }
      const updateData = {
        restaurantName: editRestaurant.restaurantName,
        address: editRestaurant.address,
        phoneNumber: editRestaurant.phoneNumber,
        cuisineType: editRestaurant.cuisineType,
        email: editRestaurant.email,
        logo: logoUrl,
        openingTime: editRestaurant.openingTime,
        closingTime: editRestaurant.closingTime,
      };
      const res = await api.post("auth/update-user", updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Fetch the latest restaurant data
      const updatedRestaurantRes = await api.get("auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRestaurant(updatedRestaurantRes.data);
      setShowEditModal(false);
      alert("Restaurant information updated successfully!");
    } catch (err) {
      alert("Failed to update restaurant information.");
    } finally {
      setIsUpdatingRestaurant(false);
    }
  };
  const [menuItems, setMenuItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price: "",
    category: "mains",
    image: "",
    type: "veg", // default to veg
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const {
    fetchRestaurantOrders,
    getRestaurantOrders,
    updateOrderStatus: contextUpdateOrderStatus,
    startPolling,
    stopPolling,
  } = useOrder();

  useEffect(() => {
    const fetchRestaurant = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      try {
        const response = await api.get("auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRestaurant(response.data);
        // Fetch menu items and orders after restaurant info is loaded
        if (response.data && response.data.id) {
          const menuRes = await api.get(`menu/restaurant/${response.data.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setMenuItems(menuRes.data);

          // Fetch orders using OrderContext
          await fetchRestaurantOrders(response.data.id);

          // Start polling for order updates
          startPolling(response.data.id);
        }
      } catch (err) {
        navigate("/login");
      } finally {
        setIsLoading(false);
      }
    };
    fetchRestaurant();

    // Cleanup polling on unmount
    return () => {
      if (restaurant?.id) {
        stopPolling(restaurant.id);
      }
    };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleAddMenuItem = async () => {
    if (!restaurant || !restaurant.id) {
      alert("Restaurant not loaded");
      return;
    }
    const token = localStorage.getItem("token");
    try {
      await api.post(`menu/add/${restaurant.id}`, [newItem], {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Fetch updated menu after adding
      const menuRes = await api.get(`menu/restaurant/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
      setNewItem({
        name: "",
        description: "",
        price: "",
        category: "mains",
        image: "",
        type: "veg",
      });
      setShowAddItem(false);
      alert("Menu item added successfully!");
    } catch (error) {
      console.error("Error adding menu item:", error);
      alert("Failed to add menu item. Please try again.");
    }
  };

  // Batch add handler for bulk upload
  const handleAddMenuItems = async (items) => {
    if (!restaurant || !restaurant.id) {
      alert("Restaurant not loaded");
      return;
    }
    const token = localStorage.getItem("token");
    try {
      await api.post(`menu/add/${restaurant.id}`, items, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Fetch updated menu after adding
      const menuRes = await api.get(`menu/restaurant/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
      alert("Menu items added successfully!");
    } catch (error) {
      console.error("Error adding menu items:", error);
      alert("Failed to add menu items. Please try again.");
    }
  };

  const handleDeleteMenuItem = async (itemId) => {
    if (!restaurant || !restaurant.id) {
      alert("Restaurant not loaded");
      return;
    }
    const token = localStorage.getItem("token");
    try {
      await api.delete(`menu/delete/${restaurant.id}/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Fetch updated menu after deleting
      const menuRes = await api.get(`menu/restaurant/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
      alert("Menu item deleted successfully!");
    } catch (error) {
      console.error("Error deleting menu item:", error);
      alert("Failed to delete menu item. Please try again.");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      if (restaurant && restaurant.id) {
        await contextUpdateOrderStatus(restaurant.id, orderId, status);
        alert("Order status updated successfully!");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("Failed to update order status. Please try again.");
    }
  };

  const generateQRCode = () => {
    const qrUrl = `${window.location.origin}/menu/${restaurant.id}`;
    return qrUrl;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateQRCode());
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = generateQRCode();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    }
  };

  if (!restaurant || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 mx-auto mb-4"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-indigo-600 absolute top-0 left-1/2 transform -translate-x-1/2"></div>
          </div>
          <div className="space-y-2 animate-pulse">
            <p className="text-gray-600 font-medium">Loading Dashboard...</p>
            <div className="w-32 h-2 bg-gray-200 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  const orders = getRestaurantOrders(restaurant.id);
  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter((o) => o.status === "pending").length,
    completedOrders: orders.filter((o) => o.status === "completed").length,
    totalRevenue: orders.reduce(
      (sum, order) => sum + (order.totalPrice || 0),
      0
    ),
  };

  const tabs = [
    { id: "menu", name: "Menu", icon: MenuIcon, color: "emerald" },
    { id: "qr", name: "QR Code", icon: QrCode, color: "orange" },
    { id: "orders", name: "Orders", icon: ShoppingCart, color: "blue" },
    { id: "overview", name: "Overview", icon: BarChart3, color: "indigo" },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "from-yellow-500 to-orange-500";
      case "preparing":
        return "from-blue-500 to-indigo-500";
      case "ready":
        return "from-green-500 to-emerald-500";
      case "completed":
        return "from-gray-500 to-slate-500";
      case "cancelled":
        return "from-red-500 to-pink-500";
      default:
        return "from-gray-500 to-slate-500";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Edit Restaurant Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
              onClick={() => setShowEditModal(false)}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Edit Restaurant Info</h2>
            <div className="space-y-3">
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Restaurant Name"
                value={editRestaurant.restaurantName}
                onChange={(e) =>
                  setEditRestaurant({
                    ...editRestaurant,
                    restaurantName: e.target.value,
                  })
                }
              />
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Address"
                value={editRestaurant.address}
                onChange={(e) =>
                  setEditRestaurant({
                    ...editRestaurant,
                    address: e.target.value,
                  })
                }
              />
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Phone Number"
                value={editRestaurant.phoneNumber}
                onChange={(e) =>
                  setEditRestaurant({
                    ...editRestaurant,
                    phoneNumber: e.target.value,
                  })
                }
              />
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Cuisine Type"
                value={editRestaurant.cuisineType}
                onChange={(e) =>
                  setEditRestaurant({
                    ...editRestaurant,
                    cuisineType: e.target.value,
                  })
                }
              />
              <input
                type="email"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Email"
                value={editRestaurant.email}
                onChange={(e) =>
                  setEditRestaurant({
                    ...editRestaurant,
                    email: e.target.value,
                  })
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opening Time
                  </label>
                  <input
                    type="time"
                    className="w-full border rounded-lg px-3 py-2"
                    value={editRestaurant.openingTime || ""}
                    onChange={(e) =>
                      setEditRestaurant({
                        ...editRestaurant,
                        openingTime: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Closing Time
                  </label>
                  <input
                    type="time"
                    className="w-full border rounded-lg px-3 py-2"
                    value={editRestaurant.closingTime || ""}
                    onChange={(e) =>
                      setEditRestaurant({
                        ...editRestaurant,
                        closingTime: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full border rounded-lg px-3 py-2"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                />
                {logoUploading && (
                  <div className="text-xs text-emerald-600 mt-1">
                    Uploading...
                  </div>
                )}
                {logoUploadError && (
                  <div className="text-xs text-red-600 mt-1">
                    {logoUploadError}
                  </div>
                )}
                {editRestaurant.logo && !logoFile && (
                  <img
                    src={editRestaurant.logo}
                    alt="Logo"
                    className="mt-2 h-12"
                  />
                )}
              </div>
            </div>
            <button
              className={`mt-5 w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700 flex items-center justify-center ${
                isUpdatingRestaurant ? "opacity-70 cursor-not-allowed" : ""
              }`}
              onClick={
                isUpdatingRestaurant ? undefined : handleUpdateRestaurant
              }
              disabled={isUpdatingRestaurant}
            >
              {isUpdatingRestaurant ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      )}
      <DashboardHeader
        restaurant={restaurant}
        handleLogout={handleLogout}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogoClick={handleEditRestaurant}
      />
      {/* Remove the separate Edit Restaurant Info button, now handled by logo click */}

      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl mx-auto">
        {activeTab === "overview" && restaurant?.id && (
          <OverviewStats
            stats={stats}
            orders={getRestaurantOrders(restaurant.id)}
            getStatusColor={getStatusColor}
          />
        )}

        {activeTab === "orders" && restaurant?.id && (
          <OrdersList
            orders={getRestaurantOrders(restaurant.id)}
            updateOrderStatus={updateOrderStatus}
            getStatusColor={getStatusColor}
          />
        )}

        {activeTab === "menu" && (
          <MenuManager
            menuItems={menuItems}
            setMenuItems={setMenuItems}
            handleAddMenuItem={handleAddMenuItem}
            handleAddMenuItems={handleAddMenuItems}
            handleDeleteMenuItem={handleDeleteMenuItem}
            showAddItem={showAddItem}
            setShowAddItem={setShowAddItem}
            newItem={newItem}
            setNewItem={setNewItem}
            restaurantId={restaurant?.id}
          />
        )}

        {activeTab === "qr" && (
          <QRCodeSection
            generateQRCode={generateQRCode}
            copyToClipboard={copyToClipboard}
            showCopySuccess={showCopySuccess}
          />
        )}
      </div>
    </div>
  );
};

export default RestaurantDashboard;
